// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import styles from './Crumb.module.css';

export interface CrumbProps {
  /**
   * The case list (`ALL_CASES_PATH`), as the crumb's first step: a plain
   * link, i.e. a page load out of the case. Leave it out where there is no
   * list to go back to (a share link is bound to its one case).
   */
  casesHref?: string;
  /** The case's display name; a dash while loading. */
  caseName?: string;
  /**
   * Back to hele bygningen (clears room and storey). When given, the case
   * name is a button; leave it out when nothing is focused.
   */
  onCase?: (() => void) | null;
  /**
   * The storey level (only when the building has storeys). `current`: the
   * storey is the innermost focus — shown as a chip with ×, `onClick` steps
   * back to hele bygningen; otherwise a room is focused under it and
   * `onClick` steps back to the whole storey.
   */
  storey?: { name: string; current: boolean; onClick: () => void } | null;
  /** A focused room, with the way back out of it (`back`: where × leads; default hele bygningen). */
  room?: { name: string; onClear: () => void; back?: string } | null;
  /** Static second step instead of the room (the ToolFrame's page title). */
  here?: string;
}

/**
 * Sager › Sag › Hele bygningen — "Sager" leaves the case for the case list —
 * then Sag › Hele bygningen, Sag › <etage> ×, Sag › <etage> › <rum> × — each
 * level a way back out: the case name to hele bygningen, the storey to the
 * whole storey, × one step out of the innermost focus.
 */
export function Crumb({ casesHref, caseName, onCase, storey, room, here }: CrumbProps) {
  const name = caseName ?? '–';
  return (
    <span className={styles.crumb}>
      {casesHref && (
        <>
          {/* A page load, not a router link: the list is outside the case's router. */}
          <a className={styles.level} href={casesHref} title="Tilbage til alle sager">
            Sager
          </a>
          <span aria-hidden="true">›</span>
        </>
      )}
      {onCase ? (
        <button
          type="button"
          className={styles.caseButton}
          onClick={onCase}
          title="Tilbage til hele bygningen"
          aria-label={`${name} — tilbage til hele bygningen`}
        >
          {name}
        </button>
      ) : (
        <b className={styles.case}>{name}</b>
      )}
      <span aria-hidden="true">›</span>
      {storey &&
        (storey.current ? (
          <button
            type="button"
            className={styles.room}
            onClick={storey.onClick}
            title="Tilbage til hele bygningen"
            aria-label={`${storey.name} — tilbage til hele bygningen`}
          >
            {storey.name} <span aria-hidden="true">×</span>
          </button>
        ) : (
          <button
            type="button"
            className={styles.level}
            onClick={storey.onClick}
            title={`Vis hele ${storey.name}`}
            aria-label={`${storey.name} — vis hele etagen`}
          >
            {storey.name}
          </button>
        ))}
      {storey && room && <span aria-hidden="true">›</span>}
      {room ? (
        <button
          type="button"
          className={styles.room}
          onClick={room.onClear}
          title={`Tilbage til ${room.back ?? 'hele bygningen'}`}
          aria-label={`${room.name} — tilbage til ${room.back ?? 'hele bygningen'}`}
        >
          {room.name} <span aria-hidden="true">×</span>
        </button>
      ) : (
        !storey && <span className={styles.here}>{here ?? 'Hele bygningen'}</span>
      )}
    </span>
  );
}
